import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

const NursingCollegeHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navigate = useNavigate();
  const dropdownTimeoutRef = useRef(null);

  // Complete route mapping for ALL pages
  const routeMap = {
    // Home
    "Home": "/",
    
    // About RDS dropdown - COMPLETE
    "Vision & Mission": "/vision-mission",
    "University": "/university",
    "Principal Message": "/principal-message",
    "Chairman’s Message": "/chairman-message",
    
    // Courses dropdown - COMPLETE with all course pages
    "ANM": "/courses/anm",
    "GNM": "/courses/gnm",
    "B.Sc.Nursing": "/courses/bsc-nursing",
    "Fee Structure": "/fee-structure",
    
    // Admission dropdown - COMPLETE
    "Registration Form": "/admission/registration",
    "Prospectus": "/prospectus",
    "RDS - Affiliation": "/affiliation",
    
    // Main navigation - COMPLETE
    "Placement": "/placement",
    "Gallery": "/gallery",
    "Contact": "/contact",
    
    // Additional pages that might be needed
    "Admission": "/admission",
    "Courses": "/nursing-courses",
  };

  // Complete navItems with ALL dropdown items
  const navItems = [
    { name: "Home", href: "/", hasDropdown: false },
    {
      name: "About RDS",
      href: "#",
      hasDropdown: true,
      dropdownItems: [
        "Vision & Mission",
        "University",
        "Principal Message",
        "Chairman’s Message",
      ],
    },
    {
      name: "Courses",
      href: "#",
      hasDropdown: true,
      dropdownItems: [
        "GNM", 
        "B.Sc.Nursing", 
        "Fee Structure"
      ],
    },
    {
      name: "Admission",
      href: "#",
      hasDropdown: true,
      dropdownItems: [
        "Registration Form", 
        "Prospectus", 
        "RDS - Affiliation", 
      ],
    },
    { name: "Placement", href: "/placement", hasDropdown: false },
    { name: "Gallery", href: "/gallery", hasDropdown: false },
    { name: "Contact", href: "/contact", hasDropdown: false },
  ];

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, []);

  // Color coding based on section
  const getColorClasses = (itemName) => {
    // Red section - Home and About RDS
    if (itemName === "Home" || 
        itemName === "About RDS" || 
        itemName === "Vision & Mission" || 
        itemName === "University" || 
        itemName === "Principal Message" || 
        itemName === "Chairman’s Message")
      return "text-red-600 hover:bg-red-50 focus:ring-red-200 active:bg-red-100";
    
    // Blue section - Courses and Admission
    else if (itemName === "Courses" || 
             itemName === "Admission" || 
             itemName === "ANM" || 
             itemName === "GNM" || 
             itemName === "B.Sc.Nursing" || 
             itemName === "Fee Structure" || 
             itemName === "Registration Form" || 
             itemName === "Prospectus" || 
             itemName === "RDS - Affiliation" || 
             itemName === "Committee")
      return "text-blue-600 hover:bg-blue-50 focus:ring-blue-200 active:bg-blue-100";
    
    // Green section - Placement, Gallery, Contact
    else
      return "text-green-600 hover:bg-green-50 focus:ring-green-200 active:bg-green-100";
  };

  // Dot colors for mobile menu
  const getDotColor = (itemName) => {
    if (itemName === "Home" || 
        itemName === "About RDS" || 
        itemName === "Vision & Mission" || 
        itemName === "University" || 
        itemName === "Principal Message" || 
        itemName === "Chairman’s Message") 
      return "bg-red-500";
    
    if (itemName === "Courses" || 
        itemName === "Admission" || 
        itemName === "ANM" || 
        itemName === "GNM" || 
        itemName === "B.Sc.Nursing" || 
        itemName === "Fee Structure" || 
        itemName === "Registration Form" || 
        itemName === "Prospectus" || 
        itemName === "RDS - Affiliation" || 
        itemName === "Committee") 
      return "bg-blue-500";
    
    return "bg-green-500";
  };

  // Handle mouse enter for dropdown
  const handleMouseEnter = (itemName) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setOpenDropdown(itemName);
  };

  // Handle mouse leave for dropdown with delay
  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 200); // 200ms delay to allow clicking on dropdown items
  };

  // Handle dropdown item clicks
  const handleDropdownClick = (subItem) => {
    const route = routeMap[subItem];
    if (route) {
      navigate(route);
    } else {
      console.log(`No route mapped for: ${subItem}`);
    }
    setIsMenuOpen(false);
    setOpenDropdown(null);
  };

  // Handle main navigation clicks
  const handleNavClick = (item) => {
    if (!item.hasDropdown) {
      navigate(item.href);
      setIsMenuOpen(false);
    }
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50 font-sans">
      {/* Top Bar with Contact Info */}
      <div className="bg-gradient-to-r from-red-600 via-blue-600 to-green-600 text-white py-2 px-4 text-sm">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-1 sm:gap-0">
          <Link to="/" className="font-medium tracking-wide flex items-center gap-1 hover:opacity-90">
            <span className="hidden xs:inline">🏥</span> R.D.S. NURSING & PARAMEDICAL COLLEGE
          </Link>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <a
              href="tel:+917607094014"
              className="flex items-center gap-1 hover:underline transition"
            >
              <span className="text-red-200">📞</span> +917607094014
            </a>
            <a
              href="tel:+918127918114"
              className="flex items-center gap-1 hover:underline transition"
            >
              <span className="text-blue-200">📞</span>
            </a>
            <a
              href="mailto:rdsnursing@gmail.com"
              className="flex items-center gap-1 hover:underline transition"
            >
              <span className="text-green-200">✉️</span> rdsnursing@gmail.com
            </a>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <div className="max-w-7xl mx-auto px-4 py-1 flex justify-between items-center">
        {/* Logo - Links to Home */}
        <Link to="/" className="flex items-center">
          <img
            src="/logo.png"
            alt="RDS Nursing College"
            className="h-16 w-auto object-contain"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-1 lg:space-x-2 relative">
          {navItems.map((item) => (
            <div
              key={item.name}
              className="relative"
              onMouseEnter={() => item.hasDropdown && handleMouseEnter(item.name)}
              onMouseLeave={handleMouseLeave}
            >
              {item.hasDropdown ? (
                <button
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${getColorClasses(
                    item.name
                  )} cursor-pointer hover:shadow-sm active:scale-95`}
                >
                  {item.name}
                  <svg
                    className="inline-block w-4 h-4 ml-1 -mt-0.5 transition-transform duration-200"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    style={{ transform: openDropdown === item.name ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              ) : (
                <Link
                  to={item.href}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${getColorClasses(
                    item.name
                  )} cursor-pointer hover:shadow-sm active:scale-95 inline-block`}
                >
                  {item.name}
                </Link>
              )}

              {/* Dropdown Menu - All items linked */}
              {item.hasDropdown && openDropdown === item.name && (
                <div 
                  className="absolute left-0 mt-1 w-56 bg-white rounded-md shadow-lg border border-gray-100 py-2 z-50 animate-fadeIn"
                  onMouseEnter={() => handleMouseEnter(item.name)}
                  onMouseLeave={handleMouseLeave}
                >
                  {item.dropdownItems.map((subItem) => (
                    <button
                      key={subItem}
                      onClick={() => handleDropdownClick(subItem)}
                      className={`w-full text-left px-4 py-2.5 text-sm ${getColorClasses(
                        subItem
                      )} transition-all duration-200 hover:pl-6 hover:font-medium cursor-pointer border-l-2 border-transparent hover:border-current active:scale-[0.98]`}
                    >
                      {subItem}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-300 active:bg-gray-200 transition-all duration-200"
          aria-label="Toggle menu"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            {isMenuOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu - All items linked */}
      {isMenuOpen && (
        <nav className="md:hidden bg-white border-t border-gray-100 px-2 py-3 space-y-2 shadow-inner max-h-[80vh] overflow-y-auto">
          {navItems.map((item) => (
            <div key={item.name} className="space-y-1">
              <div
                className={`flex items-center justify-between px-4 py-3 rounded-lg text-base font-medium ${getColorClasses(
                  item.name
                )} hover:bg-gray-50 transition-all duration-200 cursor-pointer active:scale-[0.99] active:bg-opacity-70`}
                onClick={() => {
                  if (!item.hasDropdown) {
                    handleNavClick(item);
                  } else {
                    setOpenDropdown(openDropdown === item.name ? null : item.name);
                  }
                }}
              >
                <span className="flex items-center gap-3">
                  <span
                    className={`w-2 h-2 rounded-full ${getDotColor(
                      item.name
                    )}`}
                  ></span>
                  {item.name}
                </span>
                {item.hasDropdown && (
                  <svg
                    className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                      openDropdown === item.name ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                )}
              </div>

              {/* Mobile Dropdown Items - All linked */}
              {item.hasDropdown && openDropdown === item.name && (
                <div className="ml-6 pl-2 border-l-2 border-gray-200 space-y-1 animate-slideDown">
                  {item.dropdownItems.map((subItem) => (
                    <button
                      key={subItem}
                      onClick={() => handleDropdownClick(subItem)}
                      className={`w-full text-left flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm ${getColorClasses(
                        subItem
                      )} hover:bg-gray-50 transition-all duration-200 active:scale-[0.98] active:bg-opacity-70 cursor-pointer`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${getDotColor(
                          subItem
                        )}`}
                      ></span>
                      {subItem}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Mobile Contact Info */}
          <div className="pt-3 mt-2 border-t border-gray-200 grid grid-cols-1 gap-2 px-4">
            <a
              href="tel:+917607094014"
              className="flex items-center gap-2 text-sm text-red-600 hover:bg-red-50 p-2 rounded-lg transition-all duration-200 active:scale-[0.98]"
            >
              <span>📞</span> +917607094014
            </a>
            <a
              href="tel:+918127918114"
              className="flex items-center gap-2 text-sm text-blue-600 hover:bg-blue-50 p-2 rounded-lg transition-all duration-200 active:scale-[0.98]"
            >
              <span>📞</span>
            </a>
            <a
              href="mailto:rdsnursing@gmail.com"
              className="flex items-center gap-2 text-sm text-green-600 hover:bg-green-50 p-2 rounded-lg transition-all duration-200 active:scale-[0.98]"
            >
              <span>✉️</span> rdsnursing@gmail.com
            </a>
          </div>
        </nav>
      )}
    </header>
  );
};

export default NursingCollegeHeader;